import I18n from 'I18n';
import { List, Map as ImmutableMap, Range, Set as ImmutableSet } from 'immutable';
import { memo, useEffect, useRef } from 'react';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { TableHeaderCell as TH, TableCell as TD, Table } from 'foundations-components/transitional/components/Table';
import TableCell from './TableCell';
import TableFooterCell from './TableFooterCell';
import TableHeaderCell from './TableHeaderCell';
import { ReportingLogger } from 'reporting-data/monitoring/reportingLogger';
import { getCellBlockSize } from '../tableUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROPERTIES_WITHOUT_REFERENCES = 'PropertiesWithoutReferences';
const MISSING = '@@MISSING@@';
const renderToggleAllSeriesCheckbox = ({
  toggleSeriesParams,
  data,
  isMobile
}) => {
  const {
    toggleSeriesHandler,
    toggleAllSeriesHandler,
    checkedSeries,
    seriesToggleDisabled,
    dimension
  } = toggleSeriesParams;
  if (toggleAllSeriesHandler) {
    const disabled = typeof seriesToggleDisabled === 'function' ? false : seriesToggleDisabled && seriesToggleDisabled.get('disabled');
    const tooltip = seriesToggleDisabled && typeof seriesToggleDisabled !== 'function' && seriesToggleDisabled.get('disabled') && seriesToggleDisabled.get('tooltip');
    const allProperties = ImmutableSet(data.getIn(['primary', 'data'], List()).map(datum => datum.get(dimension || '')));
    const allChecked = checkedSeries && allProperties.isSubset(checkedSeries);
    const checkbox = /*#__PURE__*/_jsx(Checkbox, {
      "aria-label": "check-all",
      checked: allChecked,
      disabled: disabled,
      indeterminate: !allChecked && checkedSeries && checkedSeries.size > 0,
      innerPadding: "none",
      onChange: e => {
        e.preventDefault();
        toggleAllSeriesHandler(allChecked ? ImmutableSet() : allProperties);
      }
    });
    if (tooltip) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: tooltip,
        placement: "top right",
        children: /*#__PURE__*/_jsx(TH, {
          inlineSize: "1rem",
          blockSize: getCellBlockSize(isMobile),
          "data-test-id": `checkbox-col-th-alltoggle-${allChecked}`,
          children: checkbox
        })
      }, `alltoggle-${allChecked}`);
    } else {
      return /*#__PURE__*/_jsx(TH, {
        inlineSize: "1rem",
        blockSize: getCellBlockSize(isMobile),
        "data-test-id": `checkbox-col-th-alltoggle-${allChecked}`,
        children: checkbox
      }, `alltoggle-${allChecked}`);
    }
  } else if (toggleSeriesHandler) {
    return /*#__PURE__*/_jsx(TH, {
      inlineSize: "1rem",
      blockSize: getCellBlockSize(isMobile),
      "data-test-id": "checkbox-col-th-alltoggle"
    }, "alltoggle");
  } else {
    return null;
  }
};
const renderToggleSeriesCheckbox = ({
  toggleSeriesParams,
  data,
  rowIndex,
  isMobile
}) => {
  const {
    toggleSeriesHandler,
    toggleAllSeriesHandler,
    checkedSeries,
    seriesToggleDisabled,
    colors,
    dimension
  } = toggleSeriesParams;
  const row = data.getIn(['primary', 'data', rowIndex], ImmutableMap());
  const key = row.get(dimension);
  if (toggleSeriesHandler) {
    const color = colors && colors.get(key) || '';
    const checked = checkedSeries && checkedSeries.includes(key) && key !== MISSING;
    const disabled = typeof seriesToggleDisabled === 'function' ? seriesToggleDisabled(row) || key === MISSING : seriesToggleDisabled && seriesToggleDisabled.get('disabled');
    const tooltip = seriesToggleDisabled && typeof seriesToggleDisabled !== 'function' ? seriesToggleDisabled.get('disabled') && seriesToggleDisabled.get('tooltip') : disabled ? I18n.text('reporting-visualizations.table.checkbox.disabled') : null;
    const checkbox =
    /*#__PURE__*/
    /*
      TODO: This component requires trellis colors but hexcodes are currently being passed
      down from the report builder. Checkboxes are currently gray. This issue is being tracked here:
      https://git.hubteam.com/HubSpot/trellis-forum/discussions/101
    */
    /* @bend-expect trellis-tokenized */
    _jsx(Checkbox, {
      "aria-label": "check",
      checked: checked,
      disabled: disabled,
      onChange: e => {
        e.preventDefault();
        toggleSeriesHandler(key);
      },
      color: color
    });
    if (tooltip) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: tooltip,
        placement: "top right",
        children: /*#__PURE__*/_jsx(TD, {
          inlineSize: "1rem",
          blockSize: getCellBlockSize(isMobile),
          "data-test-id": `checkbox-col-td-${key}-${checked}`,
          children: checkbox
        })
      }, key);
    } else {
      return /*#__PURE__*/_jsx(TD, {
        inlineSize: "1rem",
        blockSize: getCellBlockSize(isMobile),
        "data-test-id": `checkbox-col-td-${key}-${checked}`,
        children: checkbox
      }, key);
    }
  } else if (toggleAllSeriesHandler) {
    return /*#__PURE__*/_jsx(TD, {
      inlineSize: "1rem",
      blockSize: getCellBlockSize(isMobile),
      "data-test-id": `checkbox-col-td-${key}`
    }, key);
  } else {
    return null;
  }
};
const findPropertiesWithoutReferenceResolvers = data => {
  const propertiesWithoutReferences = {};
  const primaryProperties = (data === null || data === void 0 ? void 0 : data.getIn(['primary', 'properties'])) || ImmutableMap();
  primaryProperties.forEach((property, key) => {
    const propertyReferences = property.get('references', ImmutableMap());
    if (propertyReferences.size === 0) {
      const propertyName = property.get('name');
      const trackerKey = `property-${propertyName}`;
      propertiesWithoutReferences[trackerKey] = {
        type: property.get('type'),
        name: propertyName,
        label: property.get('label'),
        field: key
      };
    }
  });
  return propertiesWithoutReferences;
};
const logPropertiesWithoutReferences = propertiesWithoutReferences => {
  if (Object.keys(propertiesWithoutReferences).length === 0) {
    return;
  }
  const reportingLogger = new ReportingLogger();
  reportingLogger.addAttributes(propertiesWithoutReferences);
  reportingLogger.sendPageAction(PROPERTIES_WITHOUT_REFERENCES);
};
const TableVisualization = ({
  visualization,
  onReportVisualize
}) => {
  useEffect(() => {
    onReportVisualize === null || onReportVisualize === void 0 || onReportVisualize();
  }, [onReportVisualize]);
  const {
    data,
    columns = List(),
    footer,
    events: {
      getOnPointClick
    } = {},
    showTotals = false,
    // This is because of how ts is handling checked records
    toggleSeriesParams = {},
    style: {
      isMobile
    } = {},
    onTableCellLinkClick
  } = visualization;
  const size = (data.getIn(['primary', 'data']) || data.toList().getIn([0, 'data']) || List()).count();
  const range = Range(0, size);
  const dataRef = useRef(data);
  useEffect(() => {
    const propertiesWithoutReferences = findPropertiesWithoutReferenceResolvers(dataRef.current);
    logPropertiesWithoutReferences(propertiesWithoutReferences);
  }, []);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "table-report",
    style: {
      width: '100%',
      height: '100%'
    },
    children: /*#__PURE__*/_jsxs(Table, {
      condensed: true,
      flush: true,
      bordered: true,
      tableLayout: "auto",
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          children: [renderToggleAllSeriesCheckbox({
            toggleSeriesParams,
            data,
            isMobile
          }), columns.map((column, columnIndex) => /*#__PURE__*/_jsx(TableHeaderCell, {
            column: column,
            isMobile: isMobile
          }, columnIndex))]
        })
      }), footer && showTotals ? /*#__PURE__*/_jsx("tfoot", {
        children: /*#__PURE__*/_jsxs("tr", {
          children: [toggleSeriesParams.toggleSeriesHandler ? /*#__PURE__*/_jsx(TH, {
            inlineSize: "1rem",
            blockSize: getCellBlockSize(isMobile),
            "data-test-id": "checkbox-col-th"
          }) : null, columns.map((columnWithFooter, columnIndex) => {
            const column = columnWithFooter;
            return /*#__PURE__*/_jsx(TableFooterCell, {
              column: column,
              isMobile: isMobile
            }, columnIndex);
          })]
        })
      }) : null, /*#__PURE__*/_jsx("tbody", {
        children: range.map(rowIndex => /*#__PURE__*/_jsxs("tr", {
          style: {
            whiteSpace: 'nowrap'
          },
          children: [renderToggleSeriesCheckbox({
            toggleSeriesParams,
            data,
            rowIndex,
            isMobile
          }), columns.map((column, columnIndex) => /*#__PURE__*/_jsx(TableCell, {
            index: rowIndex,
            column: column,
            getOnPointClick: getOnPointClick,
            onTableCellLinkClick: onTableCellLinkClick,
            isMobile: isMobile
          }, columnIndex))]
        }, rowIndex))
      })]
    })
  });
};
export default /*#__PURE__*/memo(TableVisualization);